import React, { memo, useMemo, useState } from 'react';
import {
  Box, Table, TableHead, TableRow, TableCell, TableBody, Typography,
  Chip, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Checkbox, CircularProgress,
} from '@mui/material';
import { toast } from 'react-toastify';
import StatusChip from '../StatusChip';
import InvButton from '../InvButton';
import { updateRfq } from '../../api/rfq';
import {
  vendorDisplayLabel, toRfqUpdatePayload, collectRfqVendorIds,
  invitationVendorId, vendorParticipationSource, isRfqPublished,
  resolveRfqVendorLabel,
} from '../../utils/rfqEntity';
import { notifyInvitedVendorsOnPublish, summarizeRfqInviteNotify } from '../../utils/rfqInviteNotify';
import { getQuoteId } from '../../utils/rfqComparison';
import { formatDate, daysUntilExpiry } from '../../utils';
import { IPD_COLORS } from '../../theme';

/** Licence state for the compliance column, and whether it bars a pharma award. */
function licenceState(vendor) {
  if (!vendor?.license_valid_till) {
    return { label: 'Not verified', tone: IPD_COLORS.textSecondary, expired: false };
  }
  const days = daysUntilExpiry(vendor.license_valid_till);
  if (days < 0) {
    return { label: `Expired ${Math.abs(days)}d ago`, tone: IPD_COLORS.error.text, expired: true };
  }
  if (days <= 30) {
    return { label: `${days}d left`, tone: IPD_COLORS.warning.text, expired: false };
  }
  return { label: 'Valid', tone: IPD_COLORS.success.text, expired: false };
}

function InviteMoreDialog({
  open, rfq, vendorDirectory, invitedIds, hasDrugItems, onClose, onSaved,
}) {
  const [picked, setPicked] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const candidates = useMemo(
    () => vendorDirectory.filter((v) => !invitedIds.includes(v.vendor_id || v.id)),
    [vendorDirectory, invitedIds],
  );

  function toggle(id) {
    setPicked((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      await updateRfq(rfq.id || rfq.rfq_id, toRfqUpdatePayload(rfq, {
        vendorIds: [...invitedIds, ...picked],
        existingPayload: rfq.rfq_payload || {},
      }));
      if (isRfqPublished(rfq.status)) {
        const notify = await notifyInvitedVendorsOnPublish(rfq.id || rfq.rfq_id, {
          rfq,
          vendorIds: picked,
        }).catch((err) => ({ sent: [], failed: [{ reason: err.message }] }));
        toast.success(`Vendors invited. ${summarizeRfqInviteNotify(notify)}`);
      }
      onSaved?.(picked.length);
      onClose?.();
    } catch (e) {
      setError(e.message || 'Could not update the invited vendor list');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: IPD_COLORS.textPrimary }}>Invite more vendors</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {candidates.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, py: 2 }}>
            Every approved vendor is already invited to this RFQ.
          </Typography>
        ) : (
          <Table size="small">
            <TableBody>
              {candidates.map((v) => {
                const id = v.vendor_id || v.id;
                const licence = licenceState(v);
                // A vendor without a live drug licence cannot be asked to bid on
                // controlled items, so the row is disabled with the reason shown.
                const barred = hasDrugItems && licence.expired;
                return (
                  <TableRow key={id}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={picked.includes(id)}
                        disabled={barred}
                        onChange={() => toggle(id)}
                        sx={{ color: IPD_COLORS.primary }}
                        inputProps={{ 'aria-label': `Invite ${vendorDisplayLabel(v)}` }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{vendorDisplayLabel(v)}</Typography>
                      {barred && (
                        <Typography sx={{ fontSize: 11, color: IPD_COLORS.error.text }}>
                          Drug licence expired — cannot bid on the controlled items in this RFQ.
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 12, color: licence.tone }}>{licence.label}</Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          onClick={handleSave}
          disabled={saving || picked.length === 0}
          variant="contained"
          sx={{ backgroundColor: IPD_COLORS.primary }}
          startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}
        >
          Invite {picked.length || ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RfqVendorsPanel({
  rfq,
  quotes = [],
  invitations = [],
  vendorDirectory = [],
  hasDrugItems,
  canEdit,
  onRefresh,
}) {
  const [inviteOpen, setInviteOpen] = useState(false);

  const invitedIds = useMemo(() => rfq?.invited_vendor_ids || [], [rfq]);
  const vendorIds = useMemo(
    () => collectRfqVendorIds(rfq, invitations, quotes),
    [rfq, invitations, quotes],
  );

  const rows = useMemo(() => vendorIds.map((vid) => {
    const vendor = vendorDirectory.find((v) => (v.vendor_id || v.id) === vid);
    const quote = quotes.find((q) => (q.vendor_id || q.vendorId) === vid);
    const invitation = invitations.find((i) => invitationVendorId(i) === vid);
    return {
      vendorId: vid,
      source: vendorParticipationSource(vid, rfq, invitation),
      label: resolveRfqVendorLabel(vid, { vendor, invitation, quote }),
      vendor,
      quote,
      invitation,
      licence: licenceState(vendor),
    };
  }), [vendorIds, vendorDirectory, quotes, invitations, rfq]);

  const responded = rows.filter((r) => r.quote).length;
  const minimum = Number(rfq?.minimum_required_quotes) || 0;
  const inviteDialog = (
    <InviteMoreDialog
      open={inviteOpen}
      rfq={rfq}
      vendorDirectory={vendorDirectory}
      invitedIds={invitedIds}
      hasDrugItems={hasDrugItems}
      onClose={() => setInviteOpen(false)}
      onSaved={onRefresh}
    />
  );

  if (vendorIds.length === 0) {
    return (
      <Box>
        <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, py: 2 }}>
          No vendors have been invited or registered on this RFQ yet.
        </Typography>
        {canEdit && (
          <InvButton size="small" tone="teal" onClick={() => setInviteOpen(true)}>
            Invite vendors
          </InvButton>
        )}
        {inviteDialog}
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
        <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>
          {responded} of {vendorIds.length} vendors have quoted
          {minimum > 0 && ` · minimum ${minimum}`}
          {rfq?.is_open_bid ? ' · Open bid registrations appear here after they verify on the public link' : ''}
        </Typography>
        {canEdit && (
          <InvButton size="small" variant="outlined" tone="teal" onClick={() => setInviteOpen(true)}>
            Invite more
          </InvButton>
        )}
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Vendor</TableCell>
              <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Source</TableCell>
              <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Approval</TableCell>
              <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Drug licence</TableCell>
              <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Response</TableCell>
              <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Quote</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.vendorId}
                sx={hasDrugItems && row.licence.expired
                  ? { borderLeft: `3px solid ${IPD_COLORS.error.text}` }
                  : undefined}
              >
                <TableCell sx={{ fontWeight: 600 }}>{row.label}</TableCell>
                <TableCell>
                  <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
                    {row.source}
                  </Typography>
                </TableCell>
                <TableCell>
                  {row.vendor
                    ? <StatusChip status={row.vendor.approval_status || 'PENDING_APPROVAL'} />
                    : <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>Not in directory</Typography>}
                </TableCell>
                <TableCell>
                  <Typography sx={{ fontSize: 12, color: row.licence.tone, fontWeight: 600 }}>
                    {row.licence.label}
                  </Typography>
                  {row.vendor?.license_valid_till && (
                    <Typography sx={{ fontSize: 11, color: IPD_COLORS.textSecondary }}>
                      {formatDate(row.vendor.license_valid_till)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={
                      row.invitation?.state === 'DECLINED'
                        ? 'Declined'
                        : row.quote
                          ? 'Responded'
                          : row.invitation?.state === 'VIEWED'
                            ? 'Viewed'
                            : 'Awaiting quote'
                    }
                    sx={{
                      height: 20, fontSize: 11, fontWeight: 600,
                      backgroundColor: row.invitation?.state === 'DECLINED'
                        ? IPD_COLORS.error.bg
                        : row.quote ? IPD_COLORS.success.bg : IPD_COLORS.selectedBg,
                      color: row.invitation?.state === 'DECLINED'
                        ? IPD_COLORS.error.text
                        : row.quote ? IPD_COLORS.success.text : IPD_COLORS.textSecondary,
                    }}
                  />
                  {row.invitation?.state === 'DECLINED' && row.invitation.decline_reason && (
                    <Typography sx={{ fontSize: 11, color: IPD_COLORS.textSecondary }}>
                      {row.invitation.decline_reason}
                      {row.invitation.decline_remarks ? ` — ${row.invitation.decline_remarks}` : ''}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {row.quote
                    ? (row.quote.quote_number || getQuoteId(row.quote))
                    : <Typography sx={{ color: IPD_COLORS.textSecondary }}>—</Typography>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {inviteDialog}
    </Box>
  );
}

export default memo(RfqVendorsPanel);
